import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  UploadEduMedia200,
  UploadEduMediaBody
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 경로를 통해 교육용 미디어 파일을 다운로드합니다. 파일의 가격 정책(무료/유료 등)에 따라 서버에서 적절한 권한 검증이 수행됩니다. 유료 콘텐츠의 경우 사용자 인증 및 구매 여부가 확인됩니다.

 * @summary 교육용 미디어 파일 다운로드
 */
export const getEduMediaByPath = (
    path: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<Blob>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/edu/media/file/${path}`,{
        responseType: 'blob',
    ...options,}
    );
  }


export const getGetEduMediaByPathQueryKey = (path: string,) => {
    return [`https://api.ugot.uk/api/edu/media/file/${path}`] as const;
    }

    
export const getGetEduMediaByPathInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getEduMediaByPath>>, TError = AxiosError<void>>(path: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEduMediaByPathQueryKey(path);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEduMediaByPath>>> = ({ signal }) => getEduMediaByPath(path, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(path),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEduMediaByPathInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getEduMediaByPath>>>
export type GetEduMediaByPathInfiniteQueryError = AxiosError<void>

/**
 * @summary 교육용 미디어 파일 다운로드
 */
export const useGetEduMediaByPathInfinite = <TData = Awaited<ReturnType<typeof getEduMediaByPath>>, TError = AxiosError<void>>(
 path: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetEduMediaByPathInfiniteQueryOptions(path,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetEduMediaByPathQueryOptions = <TData = Awaited<ReturnType<typeof getEduMediaByPath>>, TError = AxiosError<void>>(path: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEduMediaByPathQueryKey(path);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEduMediaByPath>>> = ({ signal }) => getEduMediaByPath(path, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(path),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEduMediaByPathQueryResult = NonNullable<Awaited<ReturnType<typeof getEduMediaByPath>>>
export type GetEduMediaByPathQueryError = AxiosError<void>

/**
 * @summary 교육용 미디어 파일 다운로드
 */
export const useGetEduMediaByPath = <TData = Awaited<ReturnType<typeof getEduMediaByPath>>, TError = AxiosError<void>>(
 path: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEduMediaByPath>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetEduMediaByPathQueryOptions(path,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 교육용 미디어 파일을 업로드합니다. 가격 정책(무료/유료)은 요청 파라미터로 지정됩니다. 강사나 관리자 권한이 필요합니다.

 * @summary 교육용 미디어 파일 업로드
 */
export const uploadEduMedia = (
    uploadEduMediaBody: UploadEduMediaBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UploadEduMedia200>> => {const formData = new FormData();
formData.append('file', uploadEduMediaBody.file)
formData.append('type', uploadEduMediaBody.type)
formData.append('assetContext', uploadEduMediaBody.assetContext)
formData.append('courseId', uploadEduMediaBody.courseId.toString())
if(uploadEduMediaBody.title !== undefined) {
 formData.append('title', uploadEduMediaBody.title)
 }
if(uploadEduMediaBody.description !== undefined) {
 formData.append('description', uploadEduMediaBody.description)
 }
if(uploadEduMediaBody.altText !== undefined) {
 formData.append('altText', uploadEduMediaBody.altText)
 }
if(uploadEduMediaBody.metadata !== undefined) {
 formData.append('metadata', uploadEduMediaBody.metadata)
 }

    
    return axios.post(
      `https://api.ugot.uk/api/edu/media/upload`,
      formData,options
    );
  }



export const getUploadEduMediaMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEduMedia>>, TError,{data: UploadEduMediaBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof uploadEduMedia>>, TError,{data: UploadEduMediaBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadEduMedia>>, {data: UploadEduMediaBody}> = (props) => {
          const {data} = props ?? {};

          return  uploadEduMedia(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UploadEduMediaMutationResult = NonNullable<Awaited<ReturnType<typeof uploadEduMedia>>>
    export type UploadEduMediaMutationBody = UploadEduMediaBody
    export type UploadEduMediaMutationError = AxiosError<void>

    /**
 * @summary 교육용 미디어 파일 업로드
 */
export const useUploadEduMedia = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEduMedia>>, TError,{data: UploadEduMediaBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof uploadEduMedia>>,
        TError,
        {data: UploadEduMediaBody},
        TContext
      > => {

      const mutationOptions = getUploadEduMediaMutationOptions(options);

      return useMutation(mutationOptions);
    }
    